import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import isNil from 'lodash/fp/isNil';

import { useAppSelector } from '../../configuration/setup/hooks';
import type { Notification } from '../services/notificationsTypes';

export type NotificationState = {
    entitySourceId: string | undefined;
    runsInLivemonitor: boolean;
    externalRefetchTrigger: number;
    list: Notification[] | undefined;
    afterTag?: string;
    hasNext?: boolean;
};

const initialState: NotificationState = {
    entitySourceId: undefined,
    runsInLivemonitor: false,
    externalRefetchTrigger: 0,
    list: undefined,
    afterTag: undefined,
    hasNext: undefined,
};

export const notificationSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        entitySelected: (state, action) => {
            if (!isNil(state.entitySourceId) && state.entitySourceId !== action.payload) {
                // Reset notifications list for the new assets
                state.list = undefined;
            }
            state.entitySourceId = action.payload;
        },
        runsInLivemonitor: state => {
            state.runsInLivemonitor = true;
        },
        externalRefetchTriggerChanged: state => {
            state.externalRefetchTrigger = Date.now();
        },
        markedAllNotificationsAsRead: state => {
            // Locally update the already loaded notifications including other chunks that are fetched already.
            // This ensures that all shown notifications are updated in the UI until the refetch afterwards loaded the first chunk again.
            // This also helps updating other chunks visually, even though they are not re-fetched.
            state.list = state.list?.map(notification => {
                notification.state = 'READ';
                return notification;
            });
        },
        notificationsChanged: (
            state,
            action: PayloadAction<{
                notifications: Notification[];
                entitySourceId?: string;
                afterTag?: string;
                hasNext?: boolean;
                isLoadMore?: boolean;
            }>
        ) => {
            // Guard against stale query responses: an aborted fetch from a prior entity can still resolve
            // and dispatch this action, so we ignore payloads that don't match the currently selected entity.
            const payloadEntity = action.payload.entitySourceId;
            if (payloadEntity !== undefined && payloadEntity !== state.entitySourceId) {
                return;
            }

            // Always merge the new notifications with the previous ones and make sure that previously
            // fetched notifications are updated for instance when their status has changed. This ensures that
            // loaded more notification are still shown while the new fetched ones are added to the list.

            // Remove old notifications that are no longer contained in the backend payload. This can happen
            // when there are many new notifications nd a single one is marked as read, which will then not be
            // part of the backend payload anymore. This item needs to be removed in order to update the UI.
            const storedNotifications = state.list ?? [];
            const payloadIds = new Set(action.payload.notifications.map(notification => notification.id));

            // Don't filter out existing notifications when payload is from a load more call since the previous chunked
            // notifications are not part of that payload. Filter only when it is a regular fetch.
            const syncedExistingNotifications = action.payload.isLoadMore
                ? storedNotifications
                : storedNotifications.filter(notification => payloadIds.has(notification.id));

            // Each notification is keyed by its ID. When the same ID appears again in the new data,
            // the Map overwrites the old entry — returning an updated, deduplicated list with all properties refreshed.
            const merged = new Map(
                [...syncedExistingNotifications, ...action.payload.notifications].map(notification => [
                    notification.id,
                    notification,
                ])
            );
            const mergedNotifications = Array.from(merged.values());
            state.list = mergedNotifications;

            state.hasNext = action.payload.hasNext;

            if (action.payload.afterTag !== undefined) {
                state.afterTag = action.payload.afterTag;
            }
        },
    },
});

export const {
    entitySelected,
    runsInLivemonitor,
    notificationsChanged,
    externalRefetchTriggerChanged,
    markedAllNotificationsAsRead,
} = notificationSlice.actions;

export const useEntitySourceId = () => useAppSelector(state => state.notifications.entitySourceId);
export const useRunsInLivemonitor = () => useAppSelector(state => state.notifications.runsInLivemonitor);
export const useExternalRefetchTrigger = () => useAppSelector(state => state.notifications.externalRefetchTrigger);

export const useFetchedNotifications = () => useAppSelector(state => state.notifications.list);
export const useAfterTag = () => useAppSelector(state => state.notifications.afterTag);
export const useHasNext = () => useAppSelector(state => state.notifications.hasNext);

export default notificationSlice.reducer;
